import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import { Plus, Building, FileText, Calendar, Eye, Trash2, Loader2 } from "lucide-react";
import { useParams } from "react-router-dom";
import NoAGeneratorDrawer from "../../components/noa/NoAGeneratorDrawer";
import NoAPdfDrawer from "../../components/noa/NoAPdfDrawer";
import { NoticeOfAssignment as NoAType } from "../../types/schema";
import { Schedule } from "../../store/appStore";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
  schedules?: any[];
}

export default function NoticeOfAssignment({ handleViewNoA }: { handleViewNoA: (noa: NoAType) => void }) {
  const { workflow } = useCustomerWorkflow();
  const [noAs, setNoAs] = useState<NoAType[]>([]);
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false);
  const [selectedNoA, setSelectedNoA] = useState<NoAType | null>(null);
  // const [taggedLenders, setTaggedLenders] = useState<TaggedLender[]>([]);
  const [rentalSchedules, setRentalSchedules] = useState<Schedule[]>([]);
  const { customerId } = useParams<{ customerId: string }>();
  const [noaPendingDelete, setNoaPendingDelete] = useState<NoAType | null>(null);
  const [isDeletingNoa, setIsDeletingNoa] = useState(false);
  const [isLoadingNoAs, setIsLoadingNoAs] = useState(!!customerId);

  // Common method to fetch NoAs
  const fetchNoAs = async () => {
    if (!customerId) {
      setNoAs([]);
      return;
    }
    setIsLoadingNoAs(true);
    try {
      const response = await apiService.get<{ data: NoAType[] }>(
        `/noa/get-noas?customerId=${customerId}`
      );
      setNoAs(response.data || []);
    } catch (error) {
      console.error("Error fetching NoAs:", error);
      toast.error("Failed to load Notice of Assignments");
      setNoAs([]);
    } finally {
      setIsLoadingNoAs(false);
    }
  };

  // Fetch NoA list on component mount
  useEffect(() => {
    fetchNoAs();
    getRentalSchedules();
  }, [customerId]);

  // Fetch lender tagged list on component mount
  // useEffect(() => {
  //   const fetchTaggedLenders = async () => {
  //     if (!customerId) return;
  //     try {
  //       const response = await apiService.get<{ data: TaggedLender[] }>(`/lender/get-tagged-lenders/${customerId}`);
  //       setTaggedLenders(response.data || []);
  //     } catch (error) {
  //       console.error("Error fetching tagged lenders:", error);
  //     } finally {
  //     }
  //   };

  //   fetchTaggedLenders();
  // }, [customerId]);



  const getRentalSchedules = async () => {
    if (!customerId) return;
    try {
      const response = await apiService.get<ApiResponse<Schedule[]>>(
        `/rental-schedule/get-approved-rental-schedules?customerId=${customerId}`
      );
      const data = response.schedules;
      setRentalSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching rental schedules:", error);
      setRentalSchedules([]);
    } finally {
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "secondary";
      case "Submitted": return "warning";
      case "Approved": return "warning";
      case "In Signing": return "info";
      case "Assigned": return "success";
      case "Matured": return "destructive";
      case "Active": return "success";
      default: return "secondary";
    }
  };

  const formatDisplayId = (displayId: string | undefined | null): string => {
    if (!displayId) return "—";
    const num = parseInt(displayId, 10);
    if (isNaN(num)) return displayId; // Return as-is if not a number
    if (num >= 1 && num <= 9) {
      return num.toString().padStart(2, '0');
    }
    return displayId; // Return as-is for 10+
  };

   const getFundingModeLabel = (mode: string) => {
      switch (mode) {
        case "A": return "Pre GST";
        case "B": return "Post GST";
        case "C": return "Post TDS";
        default: return mode;
      }
    };

  // const handleViewPdf = (noa: NoAType) => {
  //   setSelectedNoA(noa);
  //   setPdfDrawerOpen(true);
  // };

  const isNoaApproved = (noa: NoAType) =>
    String(noa.status ?? "").toLowerCase() === "approved";

  const confirmDeleteNoa = async () => {
    if (!noaPendingDelete) return;
    if (isNoaApproved(noaPendingDelete)) {
      toast.error("Cannot delete an approved Notice of Assignment");
      setNoaPendingDelete(null);
      return;
    }
    try {
      setIsDeletingNoa(true);
      await apiService.post(`/noa/delete-noa/${noaPendingDelete.noaId}`, {});
      toast.success("Notice of Assignment deleted");
      setNoaPendingDelete(null);
      await fetchNoAs();
      await getRentalSchedules();
    } catch (error) {
      console.error("Error deleting NoA:", error);
      toast.error("Failed to delete Notice of Assignment");
    } finally {
      setIsDeletingNoa(false);
    }
  };

  const handleAddNoA = async (noa: NoAType) => {
    try {
      const response = await apiService.post<{ data: NoAType; message?: string }>(
        `/noa/create-noa`,
        noa as any
      );
      
      if (response.data) {
        toast.success("Notice of Assignment created successfully");
        // Refresh the NoA list after creating a new one
        await fetchNoAs();
        await getRentalSchedules();
      }
    } catch (error) {
      console.error("Error creating NoA:", error);
      toast.error("Failed to create Notice of Assignment");
    }
  };


  const getDisplayStatus = (noa:any) => {
  const today = new Date();
  const lastDate = noa?.last_to_date ? new Date(noa.last_to_date) : null;
  const status = noa.status?.toLowerCase() ?? "draft";

  if (status === "draft") return "Draft";

  if (status === "approved") {
    if (lastDate) {
      if (today < lastDate) return "Active";
      return "Matured";
    }
    return "Approved";
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
};

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Notice of Assignment
              </CardTitle>
              <CardDescription>
                Create NoA with PV-based cap and funding modes. Link rental schedules to lenders with precise funding calculations.
              </CardDescription>
            </div>
            <NoAGeneratorDrawer 
              approvedSchedules={rentalSchedules}
              addNoA={handleAddNoA}
            >
              <Button className="flex items-center gap-2"
               tooltip={workflow?.createNOA?.reason}
               disabled={!workflow?.createNOA?.enabled}
              >
                <Plus className="h-4 w-4" />
                Generate Notice of Assignment
              </Button>
            </NoAGeneratorDrawer>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{noAs.length}</div>
              <div className="text-sm text-muted-foreground">Total NoAs</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{noAs.filter(n => n.status === 'Draft').length}</div>
              <div className="text-sm text-muted-foreground">Draft</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{noAs.filter(n => n.status === 'Signed').length}</div>
              <div className="text-sm text-muted-foreground">Active</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">
                ₹{noAs.reduce((sum, n) => sum + n.pvTotal, 0).toLocaleString("en-IN")}
              </div>
              <div className="text-sm text-muted-foreground">Total PV</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* NoA List */}
      <div className="relative">
        <Card className={isDeletingNoa || isLoadingNoAs ? "min-h-[200px]" : undefined}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Assignments
          </CardTitle>
          <CardDescription>
            List of NoA with caps, PV calculations, and variance analysis.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isLoadingNoAs && noAs.length === 0 ? (
            <div className="rounded-md border p-8 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No assignments yet.</p>
              <p className="text-sm">Create your first Notice of Assignment to get started.</p>
            </div>
          ) : (
            <div className="overflow-auto">
              <Table className="table-auto w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-fit whitespace-nowrap">NoA ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="w-fit whitespace-nowrap">Lender</TableHead>
                    <TableHead className="w-fit whitespace-nowrap">Schedule No.</TableHead>
                    <TableHead className="w-fit whitespace-nowrap">Funding Mode</TableHead>
                    <TableHead className="w-fit whitespace-nowrap text-right">Total Asset Value</TableHead>
                    <TableHead className="w-fit whitespace-nowrap text-right">PV Total</TableHead>
                    <TableHead className="w-fit whitespace-nowrap text-right">Total Funding</TableHead>
                    <TableHead className="w-fit whitespace-nowrap">Status</TableHead>
                    <TableHead className="w-fit whitespace-nowrap">Created</TableHead>
                    <TableHead className="w-fit whitespace-nowrap text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!isLoadingNoAs &&
                  noAs.map((noa) => {
                    const securityDepositPercent = noa.rentalSchedule?.config?.securityDepositPercent ?? 0;
                    const totalFunding = (noa.pvTotal + (noa.totalAssetValue  * securityDepositPercent) / 100) - (noa.lenderFeeAmount ?? 0);

                    const displayStatus = getDisplayStatus(noa);
                    const canDeleteNoa = !isNoaApproved(noa);

                    return (
                      <TableRow key={noa.noaId}>
                      <TableCell className="font-medium">{noa.noaId}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{noa.customer.name}</div>
                          <div className="text-sm text-muted-foreground">{noa.customer.gstin}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {noa.lenderAvatar ? (
                            <Avatar className="rounded-lg" style={{ height: "30px", width: "45px" }}>
                              <AvatarImage
                                src={
                                  noa.lenderAvatar.startsWith("http")
                                    ? noa.lenderAvatar
                                    : `${import.meta.env.VITE_API_BASE_URL}/${noa.lenderAvatar}`
                                }
                                alt={noa.lenderName}
                                className="object-contain"
                              />
                            </Avatar>
                          ) : null}
                          <div className="font-medium">{noa.lenderName}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        RS NO - {formatDisplayId(noa.rentalSchedule.displayId)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getFundingModeLabel(noa.fundingMode)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">₹{noa.fundingAmountCap.toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-right">₹{noa.pvTotal.toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-right">
                        ₹{Math.round(totalFunding).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell>
          
                        
                    <Badge variant={
                      
                      getStatusColor(displayStatus)}>
                        {displayStatus}
                      </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {new Date(noa.createdAt).toLocaleDateString('en-IN')}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View Details"
                            onClick={() => handleViewNoA(noa)}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!canDeleteNoa}
                            className={
                              canDeleteNoa
                                ? "text-destructive hover:text-destructive hover:bg-red-50"
                                : "cursor-not-allowed opacity-40 text-muted-foreground hover:bg-transparent hover:text-muted-foreground"
                            }
                            title={
                              canDeleteNoa
                                ? "Delete"
                                : "Cannot delete an approved Notice of Assignment"
                            }
                            onClick={() => {
                              if (!canDeleteNoa) return;
                              setNoaPendingDelete(noa);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        </Card>
        {(isLoadingNoAs || isDeletingNoa) && (
          <div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/60 backdrop-blur-[1px]"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium text-foreground/80">
              {isDeletingNoa ? "Deleting and refreshing…" : "Loading assignments…"}
            </p>
          </div>
        )}
      </div>

      {/* PDF Drawer */}
      {selectedNoA && (
        <NoAPdfDrawer 
          open={pdfDrawerOpen} 
          onOpenChange={setPdfDrawerOpen} 
          noa={selectedNoA} 
        />
      )}

      <AlertDialog
        open={!!noaPendingDelete}
        onOpenChange={(open) => {
          if (!open && !isDeletingNoa) setNoaPendingDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Notice of Assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the NoA from the list. This cannot be undone.
              {noaPendingDelete?.noaId != null && (
                <span className="mt-2 block font-mono text-xs text-slate-700">
                  {noaPendingDelete.noaId}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingNoa}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="border-transparent bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-600"
              disabled={isDeletingNoa}
              onClick={() => void confirmDeleteNoa()}
            >
              {isDeletingNoa ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
